import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { 
  AlertTriangle, Plus, Camera, MapPin, Send, Clock,
  CheckCircle, XCircle, Upload, Mic, Eye, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const QuickHazardPage = () => {
  const [hazards, setHazards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReportForm, setShowReportForm] = useState(false);
  const [selectedHazard, setSelectedHazard] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    description: '',
    category: 'other',
    severity: 'medium',
    location: '',
    is_anonymous: false,
    reporter_name: '',
    immediate_action_taken: '',
    photo_base64: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hazardsRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/quick-hazard`),
        axios.get(`${API}/api/quick-hazard/categories`),
        axios.get(`${API}/api/quick-hazard/stats/summary`)
      ]);
      setHazards(hazardsRes.data.hazards || []);
      setCategories(categoriesRes.data.categories || []);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitHazard = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        reporter_name: formData.is_anonymous ? null : formData.reporter_name
      };
      await axios.post(`${API}/api/quick-hazard`, submitData);
      toast({ title: 'Hazard Reported!', description: 'Your report has been submitted successfully' });
      setShowReportForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit report', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      description: '',
      category: 'other',
      severity: 'medium',
      location: '',
      is_anonymous: false,
      reporter_name: '',
      immediate_action_taken: '',
      photo_base64: null
    });
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const updateHazardStatus = async (hazardId, status, notes = '') => {
    try {
      await axios.put(`${API}/api/quick-hazard/${hazardId}`, { status, resolution_notes: notes });
      toast({ title: 'Updated', description: `Hazard marked as ${status}` });
      fetchData();
      setSelectedHazard(null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update hazard', variant: 'destructive' });
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredHazards = hazards.filter(h => {
    if (filterStatus !== 'all' && h.status !== filterStatus) return false;
    if (filterSeverity !== 'all' && h.severity !== filterSeverity) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quick Hazard Report</h1>
          <p className="text-gray-600">One-tap hazard reporting for the field</p>
        </div>
        <Button onClick={() => setShowReportForm(true)} className="bg-red-600 hover:bg-red-700">
          <AlertTriangle className="w-4 h-4 mr-2" />
          Report Hazard
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats?.total_hazards || 0}</p>
              <p className="text-sm text-gray-600">Total Reports</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-600">{stats?.new || 0}</p>
              <p className="text-sm text-gray-600">New/Open</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{stats?.resolved || 0}</p>
              <p className="text-sm text-gray-600">Resolved</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{stats?.resolution_rate || 0}%</p>
              <p className="text-sm text-gray-600">Resolution Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Severity Breakdown */}
      {stats?.by_severity && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">By Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 flex-wrap">
              {Object.entries(stats.by_severity).map(([severity, count]) => (
                <Badge key={severity} className={`${getSeverityColor(severity)} px-3 py-1`}>
                  {severity}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Select value={filterSeverity} onValueChange={setFilterSeverity}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severity</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Hazard List */}
      <div className="space-y-4">
        {filteredHazards.map((hazard) => (
          <Card key={hazard.id} className={`border-l-4 hover:shadow-lg transition-shadow cursor-pointer ${getSeverityColor(hazard.severity)}`} onClick={() => setSelectedHazard(hazard)}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getSeverityColor(hazard.severity)}>{hazard.severity}</Badge>
                    <Badge className={getStatusColor(hazard.status)}>{hazard.status}</Badge>
                    <Badge variant="outline">{hazard.category?.replace('_', ' ')}</Badge>
                  </div>
                  <p className="font-medium text-gray-900">{hazard.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" />{hazard.location}</span>
                    <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{new Date(hazard.created_at).toLocaleString()}</span>
                    {hazard.has_photo && <span className="flex items-center"><Camera className="w-4 h-4 mr-1" />Photo</span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Reported by: {hazard.reporter_name || 'Anonymous'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredHazards.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Hazards Found</h3>
            <p className="text-gray-600 mb-4">{hazards.length === 0 ? 'No hazards have been reported yet' : 'No hazards match your filters'}</p>
            <Button onClick={() => setShowReportForm(true)}>Report a Hazard</Button>
          </CardContent>
        </Card>
      )}

      {/* Report Hazard Dialog */}
      <Dialog open={showReportForm} onOpenChange={setShowReportForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
              Report Hazard
            </DialogTitle>
            <DialogDescription>Quick report - takes less than 30 seconds</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitHazard} className="space-y-4">
            <div>
              <Label>What's the hazard? *</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe the hazard you've observed..." required rows={3} />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severity</Label>
                <Select value={formData.severity} onValueChange={(v) => setFormData({...formData, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Location *</Label>
              <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="Where is this hazard?" required />
            </div>

            <div>
              <Label>Immediate Action Taken</Label>
              <Input value={formData.immediate_action_taken} onChange={(e) => setFormData({...formData, immediate_action_taken: e.target.value})} placeholder="Any steps you took to address it?" />
            </div>

            {/* Photo Upload */}
            <div>
              <Label>Add Photo</Label>
              <input type="file" accept="image/*" capture="environment" ref={fileInputRef} onChange={handlePhotoCapture} className="hidden" />
              <div className="flex gap-2 mt-1">
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" /> Take Photo
                </Button>
                {formData.photo_base64 && (
                  <Badge className="bg-green-100 text-green-800">Photo attached</Badge>
                )}
              </div>
            </div>

            {/* Anonymous Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Submit Anonymously</Label>
                <p className="text-xs text-gray-500">Your name won't be recorded</p>
              </div>
              <Switch checked={formData.is_anonymous} onCheckedChange={(v) => setFormData({...formData, is_anonymous: v})} />
            </div>

            {!formData.is_anonymous && (
              <div>
                <Label>Your Name</Label>
                <Input value={formData.reporter_name} onChange={(e) => setFormData({...formData, reporter_name: e.target.value})} placeholder="Enter your name" />
              </div>
            )}

            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700">
                <Send className="w-4 h-4 mr-2" /> Submit Report
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowReportForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hazard Detail Dialog */}
      <Dialog open={!!selectedHazard} onOpenChange={() => setSelectedHazard(null)}>
        <DialogContent className="max-w-lg">
          {selectedHazard && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge className={getSeverityColor(selectedHazard.severity)}>{selectedHazard.severity}</Badge>
                  <Badge className={getStatusColor(selectedHazard.status)}>{selectedHazard.status}</Badge>
                </div>
                <DialogTitle className="mt-2">{selectedHazard.report_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-600">Description</Label>
                  <p className="font-medium">{selectedHazard.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Category</Label>
                    <p>{selectedHazard.category?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Location</Label>
                    <p>{selectedHazard.location}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Reported By</Label>
                    <p>{selectedHazard.reporter_name || 'Anonymous'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-600">Reported At</Label>
                    <p>{new Date(selectedHazard.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {selectedHazard.immediate_action_taken && (
                  <div>
                    <Label className="text-gray-600">Immediate Action</Label>
                    <p>{selectedHazard.immediate_action_taken}</p>
                  </div>
                )}
                
                {selectedHazard.status !== 'resolved' && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button className="flex-1" variant="outline" onClick={() => updateHazardStatus(selectedHazard.id, 'in_progress')}>
                      <Clock className="w-4 h-4 mr-2" /> Mark In Progress
                    </Button>
                    <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => updateHazardStatus(selectedHazard.id, 'resolved')}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Resolve
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickHazardPage;
